import _ from 'underscore';
import { Validation } from '@bingads-webui/validation';
import { StringRules } from './string/rules';

let generateRules;

/**
 * Generate number rules
 * @param {Object} options - property define object
 * @param {I18n} i18n - provide callback getString to get message
 * @returns {Object} - return object with property type, decimalLength and range
 */
function numberRulesGenerator(options, i18n) {
  const rules = {
    type: Validation.number.type(i18n),
    decimalLength: Validation.number.size(undefined, options.decimalLength, i18n),
  };

  if (_.isFinite(options.maximum) || _.isFinite(options.minimum) || options.isPercentValue) {
    const minimum = _.isFinite(options.minimum) ? options.minimum : (options.isPercentValue && 0);
    const maximum = _.isFinite(options.maximum) ? options.maximum : (options.isPercentValue && 100);

    rules.range = Validation.number.range(minimum, maximum, i18n);
  }

  return rules;
}

/**
 * Generate enum rules
 * @param {Object} prop - object with property enum
 * @param {I18n} i18n - provide getString callback to get message
 * @returns {Object} - rule object with property enum
 */
function generateEnumRules(prop, i18n) {
  return {
    enum: Validation.enum(prop.enum, i18n),
  };
}

/**
 * Generate item rules for array
 * @param {Object} prop - define property for item in array
 * @param {I18n} i18n - provide callback getString to get message
 * @returns {Object} - return rule for item
 */
function generateItemRules(prop, i18n) {
  if (!prop.enum && prop.type === 'string') {
    return StringRules(prop, {}, i18n);
  }

  return generateRules(prop, i18n);
}

const rulesGenerator = {
  integer: (prop, i18n) => numberRulesGenerator(_.defaults({ decimalLength: 0 }, prop), i18n),
  number: (prop, i18n) => numberRulesGenerator(_.defaults({ decimalLength: 2 }, prop), i18n),
  string: () => ({}),
  boolean: (prop, i18n) => ({
    enum: Validation.enum([true, false], i18n),
  }),
  object: () => ([]),
  array: (prop, i18n) => {
    const rules = {};

    if (prop.minItems || prop.maxItems) {
      rules.size = Validation.array.size(prop.minItems, prop.maxItems, i18n);
    }

    if (prop.uniqueItems) {
      rules.unique = Validation.array.unique(i18n);
    }

    if (prop.items) {
      if (_.isArray(prop.items)) {
        const itemsValidators = _.map(prop.items, item => _.values(generateItemRules(item, i18n)));

        const additionalItemValiators = _.isBoolean(prop.additionalItems) ?
          prop.additionalItems :
          _.values(generateItemRules(prop.additionalItems, i18n));

        rules.items = Validation.array.items(itemsValidators, additionalItemValiators, i18n);
      } else {
        const itemValidators = _.values(generateItemRules(prop.items, i18n));

        rules.item = Validation.array.item(itemValidators, i18n);
      }
    }

    return rules;
  },
  datetime: () => ([]),
};

/**
 * Generate rules base on property define
 * @param {Object} prop - property define object
 * @param {I18n} i18n - provide callback getString to get message
 * @returns {Object} - return rule for property
 */
generateRules = (prop, i18n) => {
  if (prop.enum) {
    return generateEnumRules(prop, i18n);
  }

  if (_.has(rulesGenerator, prop.type)) {
    return rulesGenerator[prop.type](prop, i18n);
  }

  return {};
};

/**
 * convert json schema limitation to rules for property
 * @param {Object} prop - property defined in schema, include property type, limitation, etc.
 * @param {JsonSchema} schema - define the global limitation for parent model,
 * only has required limitation now
 * @param {string} key - property name of rule
 * @param {CustomerValidators} customValidators - custom validators,
 * allow user to define validatator for this property
 * @param {I18n} i18n - provide callback getString to get error message
 * @return {array} an array of rule
 */
export function Rules(prop, schema, key, customValidators, i18n) {
  const rules = generateRules(prop, i18n);

  if (prop.required || _.contains(schema.required, key)) {
    const allowSpace = _.result(prop.required, 'allowSpace', false);

    rules.required = Validation.required(allowSpace, i18n);
  }

  if (_.isObject(customValidators) && _.isObject(customValidators[key])) {
    rules.custom = Validation.custom(
      customValidators[key].isValid,
      customValidators[key].message, false, i18n
    );
  }

  rules.passive = Validation.passive(i18n);

  return rules;
}
